import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Bell, Plus, Pencil, Trash2 } from "lucide-react";

import { isArray } from "lodash";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppTooltip from "../../components/AppTooltip";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];
    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const idx = state.findIndex((u) => u.id === announcement.id);
        if (idx !== -1) state[idx] = announcement;
        else newAnnouncements.push(announcement);
      });
    }
    return [...state, ...newAnnouncements];
  }
  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const idx = state.findIndex((u) => u.id === announcement.id);
    if (idx !== -1) {
      state[idx] = announcement;
      return [...state];
    }
    return [announcement, ...state];
  }
  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;
    const idx = state.findIndex((u) => u.id === announcementId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 0,
      overflowY: "hidden",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
    },
    actionsCell: {
      whiteSpace: "nowrap",
      paddingRight: "16px !important",
      "& > *": {
        marginLeft: 4,
      },
      "& > *:first-child": {
        marginLeft: 0,
      },
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    statusActive: {
      background: isDark ? "rgba(76,175,80,0.15)" : "rgba(76,175,80,0.12)",
      color: isDark ? "#81c784" : "#2e7d32",
    },
    statusInactive: {
      background: isDark ? "rgba(229,57,53,0.18)" : "rgba(229,57,53,0.10)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    priorityBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    priorityHigh: {
      background: isDark ? "rgba(229,57,53,0.18)" : "rgba(229,57,53,0.10)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    priorityMedium: {
      background: isDark ? "rgba(255,193,7,0.18)" : "rgba(255,193,7,0.20)",
      color: isDark ? "#ffd54f" : "#a06600",
    },
    priorityLow: {
      background: isDark ? "rgba(33,150,243,0.18)" : "rgba(33,150,243,0.10)",
      color: isDark ? "#90caf9" : "#1565c0",
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: isDark ? "#666" : "#999",
      fontSize: 13.5,
    },
    mutedCell: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontStyle: "italic",
    },
  };
});

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { ticketSearchParam, clear } = useSearch();
  const announcementSubtitle = i18n.t("announcements.subtitle");

  const [loading, setLoading] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [announcements, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    if (!user.super) {
      toast.error(i18n.t("announcements.toasts.noPermission"));
      setTimeout(() => history.push(`/`), 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      dispatch({ type: "RESET" });
      const { data } = await api.get("/announcements/", {
        params: { searchParam: "", pageNumber: 1 },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user.super) fetchAnnouncements();
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.companyId) return;
    const onCompanyAnnouncement = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    };
    socket.on(`company-announcement`, onCompanyAnnouncement);
    return () => {
      socket.off(`company-announcement`, onCompanyAnnouncement);
    };
  }, [user, socket]);

  const filtered = useMemo(() => {
    const term = (ticketSearchParam || "").trim().toLowerCase();
    if (!term) return announcements;
    return announcements.filter(
      (a) =>
        a.title?.toLowerCase().includes(term) ||
        a.text?.toLowerCase().includes(term)
    );
  }, [announcements, ticketSearchParam]);

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
        await api.delete(`/announcements/${announcement.id}/media-upload`);
      await api.delete(`/announcements/${announcement.id}`);
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    fetchAnnouncements();
  };

  const renderPriority = (val) => {
    let label = "-";
    let cls = classes.priorityLow;
    if (val === 1) {
      label = i18n.t("announcements.dialog.form.high");
      cls = classes.priorityHigh;
    } else if (val === 2) {
      label = i18n.t("announcements.dialog.form.medium");
      cls = classes.priorityMedium;
    } else if (val === 3) {
      label = i18n.t("announcements.dialog.form.low");
      cls = classes.priorityLow;
    }
    return <span className={`${classes.priorityBadge} ${cls}`}>{label}</span>;
  };

  const renderStatus = (status) => {
    const active = !!status;
    return (
      <span
        className={`${classes.statusBadge} ${active ? classes.statusActive : classes.statusInactive}`}
      >
        <span
          className={classes.statusDot}
          style={{ background: active ? "#4caf50" : "#e53935" }}
        />
        {active ? i18n.t("announcements.active") : i18n.t("announcements.inactive")}
      </span>
    );
  };

  if (!user.super) return null;

  return (
    <MainContainer>
      <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <Bell size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("announcements.title")}
              <span className={classes.countChip}>{filtered.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {announcementSubtitle === "announcements.subtitle"
                ? "Gerencie informativos enviados aos usuários da plataforma"
                : announcementSubtitle}
            </div>
          </div>
        </div>

        <Button className={classes.newBtn} onClick={handleOpenAnnouncementModal}>
          <Plus size={16} strokeWidth={2} />
          {i18n.t("announcements.buttons.add")}
        </Button>
      </div>

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <div className={classes.tableScroll}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="left">{i18n.t("announcements.table.title")}</TableCell>
                <TableCell align="center">{i18n.t("announcements.table.priority")}</TableCell>
                <TableCell align="center">{i18n.t("announcements.table.mediaName")}</TableCell>
                <TableCell align="center">{i18n.t("announcements.table.status")}</TableCell>
                <TableCell align="right" className={classes.actionsCell}>
                  {i18n.t("announcements.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && announcements.length === 0 ? (
                <TableRowSkeleton columns={5} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyState}>
                    {ticketSearchParam
                      ? i18n.t("announcements.empty.search") ||
                        "Nenhum informativo corresponde à busca."
                      : i18n.t("announcements.empty.list") ||
                        "Nenhum informativo cadastrado."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((announcement) => (
                  <TableRow key={announcement.id} className={classes.tableRow}>
                    <TableCell align="left" className={classes.nameCell}>
                      {announcement.title || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {renderPriority(announcement.priority)}
                    </TableCell>
                    <TableCell align="center">
                      {announcement.mediaName ? (
                        announcement.mediaName
                      ) : (
                        <span className={classes.mutedCell}>
                          {i18n.t("quickMessages.noAttachment")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {renderStatus(announcement.status)}
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      <AppTooltip title={i18n.t("contacts.buttons.edit") || "Editar"}>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => handleEditAnnouncement(announcement)}
                          aria-label="edit"
                        >
                          <Pencil size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                      <AppTooltip title={i18n.t("contacts.buttons.delete") || "Excluir"}>
                        <IconButton
                          size="small"
                          className={classes.deleteActionBtn}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingAnnouncement(announcement);
                          }}
                          aria-label="delete"
                        >
                          <Trash2 size={16} strokeWidth={2} />
                        </IconButton>
                      </AppTooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <AnnouncementModal
        resetPagination={fetchAnnouncements}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />

      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${deletingAnnouncement.title}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      </div>
    </MainContainer>
  );
};

export default Announcements;
